import type { ReactNode } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { HeadlineCardRoot } from './HeadlineCard.styled';

export interface HeadlineCardProps {
  title: string;
  headline: string;
  href?: string;
  /** The muted word beside the big number — `students`, `participation`, `/ student`. */
  unit?: string;
  yoy?: string;
  subline?: ReactNode;
  /** Explanatory copy behind an info icon at title height. One card uses it. */
  tooltip?: string;
  /**
   * Why this number cannot be shown. When set the card renders the reason in
   * place of the value, so an unavailable number never reads as a real one.
   */
  unavailable?: string;
}

/** The second line under the number: 12px medium muted. */
const SECONDARY_SX = {
  fontWeight: 500,
  fontSize: 12,
  lineHeight: '16px',
  color: COLORS.muted,
} as const;

/** The unit beside the number: 16px semibold muted, sitting on the number's baseline. */
const UNIT_SX = {
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '24px',
  color: COLORS.muted,
} as const;

function TitleRow({ title, tooltip }: { title: string; tooltip?: string }) {
  const heading = (
    <Typography
      variant="subtitle2"
      component="div"
      data-testid="headline-title"
      sx={{
        fontWeight: 700,
        fontSize: 16,
        lineHeight: '24px',
        color: COLORS.muted,
      }}
    >
      {title}
    </Typography>
  );

  if (!tooltip) {
    return heading;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {heading}
      <Tooltip title={tooltip} describeChild enterTouchDelay={0} leaveTouchDelay={8000}>
        <Box
          component="button"
          type="button"
          aria-label={`About ${title}`}
          data-testid="headline-tooltip-trigger"
          sx={{
            p: 0,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            color: COLORS.muted,
            lineHeight: '24px',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
      </Tooltip>
    </Box>
  );
}

export function HeadlineCard({
  title,
  headline,
  href,
  unit,
  yoy,
  subline,
  tooltip,
  unavailable,
}: HeadlineCardProps) {
  return (
    <HeadlineCardRoot data-testid="headline-card">
      <TitleRow title={title} tooltip={tooltip} />
      {unavailable ? (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            component="div"
            data-testid="headline-unavailable-value"
            sx={{
              fontWeight: 600,
              fontSize: 20,
              lineHeight: '28px',
              color: COLORS.muted,
            }}
          >
            —
          </Typography>
          <Typography
            variant="caption"
            component="div"
            data-testid="headline-unavailable-reason"
            sx={SECONDARY_SX}
          >
            {unavailable}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '4px',
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="h4"
              component="div"
              data-testid="headline-value"
              sx={{
                fontWeight: 600,
                fontSize: 32,
                lineHeight: '40px',
                color: COLORS.ink,
              }}
            >
              {href ? (
                <Link
                  component={RouterLink}
                  to={href}
                  state={APP_LINK_STATE}
                  underline="hover"
                  sx={{ color: COLORS.link }}
                >
                  {headline}
                </Link>
              ) : (
                headline
              )}
            </Typography>
            {unit ? (
              <Typography component="div" data-testid="headline-unit" sx={UNIT_SX}>
                {unit}
              </Typography>
            ) : null}
          </Box>
          {yoy ? (
            <Typography
              variant="body2"
              component="div"
              data-testid="headline-yoy"
              sx={SECONDARY_SX}
            >
              {yoy}
            </Typography>
          ) : null}
          {subline ? (
            <Typography
              variant="caption"
              component="div"
              data-testid="headline-subline"
              sx={SECONDARY_SX}
            >
              {subline}
            </Typography>
          ) : null}
        </Box>
      )}
    </HeadlineCardRoot>
  );
}
